// In-page alert-fired notification: chime + top-right pop-up, ported from
// legacy/dashboard.html's `playAlertSound`/`showNotification`/
// `dismissNotification`/`toggleAudio`/`updateAudioToggle` (audio numbers
// -- 800Hz bullish / 400Hz bearish sine, gain 0.3 -> 0.01 exponential over
// 0.5s -- are legacy's own actual values, not invented). Visuals are
// restructured to this dashboard's own existing markup convention
// (`el()` only, no innerHTML -- see util/dom.ts) and its own existing
// color tokens (styles.css already uses the same #00ff88/#ff4444/#1a2340
// hex values legacy does, so this isn't a re-theme, just a rebuild in
// this app's own component style).
//
// What legacy had that this deliberately drops: the "Reset" button.
// Legacy's alerts had a permanent `triggered` flag an alert stayed stuck
// on until a manual Reset (delete + recreate); the new AlertResponse model
// has no such field -- the server alert-engine re-arms automatically via
// cooldown_seconds (see src/alerts/evaluator.ts's docstring). There is
// nothing to "reset," so only Dismiss (close the card) is kept.

import { el } from "../util/dom";
import { formatPrice, formatRatio } from "../util/format";
import type { FiredAlert } from "../alerts/evaluator";

const AUDIO_ENABLED_KEY = "btclock_audio_enabled";
// Best-effort browser Notification permission is only ever asked for once
// per session at most, and only after the user has explicitly opted into
// audio (a real signal they want to be alerted) -- never nagged on page
// load. See buildNotificationCenter's `maybeRequestOsPermission`.
let osPermissionRequestedThisSession = false;

function loadAudioEnabled(): boolean {
  try {
    return localStorage.getItem(AUDIO_ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}

function saveAudioEnabled(value: boolean): void {
  try {
    localStorage.setItem(AUDIO_ENABLED_KEY, String(value));
  } catch {
    /* no-op -- audio toggle just won't persist across reloads */
  }
}

export interface NotificationCenter {
  root: HTMLElement; // the #notificationContainer equivalent
  audioToggle: HTMLElement; // append into the alerts panel
  fire(alert: FiredAlert, midPrice: number): void;
}

export function buildNotificationCenter(): NotificationCenter {
  let audioEnabled = loadAudioEnabled();
  let audioContext: AudioContext | null = null;
  let notificationCount = 0;

  const container = el("div", { class: "notification-container" });

  const audioIcon = el("span", {}, [audioEnabled ? "🔊" : "🔇"]);
  const audioLabel = el("span", {}, [audioEnabled ? "Audio: ON" : "Audio: OFF"]);
  const audioToggle = el(
    "label",
    { class: "audio-toggle" + (audioEnabled ? " enabled" : "") },
    [audioIcon, audioLabel]
  );
  audioToggle.addEventListener("click", () => {
    audioEnabled = !audioEnabled;
    saveAudioEnabled(audioEnabled);
    if (audioEnabled) {
      if (!audioContext) {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctor) audioContext = new Ctor();
      }
      maybeRequestOsPermission();
    }
    audioToggle.classList.toggle("enabled", audioEnabled);
    audioIcon.textContent = audioEnabled ? "🔊" : "🔇";
    audioLabel.textContent = audioEnabled ? "Audio: ON" : "Audio: OFF";
  });

  function maybeRequestOsPermission(): void {
    // Best-effort attempt at the "even if you're not looking at this tab"
    // half of the Owner's ask, via the real Notification API -- gated so
    // it never nags: at most once per session, and only once the user has
    // taken an affirmative action (enabling audio) signaling real intent
    // to be alerted, not on page load.
    if (osPermissionRequestedThisSession) return;
    osPermissionRequestedThisSession = true;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      void Notification.requestPermission();
    }
  }

  function playAlertSound(isBullish: boolean): void {
    if (!audioEnabled || !audioContext) return;
    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = isBullish ? 800 : 400;
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch {
      // Playback can fail (autoplay policy, closed context, etc.) --
      // never let a missed chime break the rest of the notification.
    }
  }

  function showNotification(alert: FiredAlert, midPrice: number): void {
    notificationCount += 1;
    const id = `notification-${notificationCount}`;
    const isBullish = alert.threshold.direction === "above";
    const icon = isBullish ? "📈" : "📉";
    const title = isBullish ? "BULLISH ALERT" : "BEARISH ALERT";
    const direction = isBullish ? "above" : "below";
    const timeText = new Date().toLocaleTimeString();

    const closeBtn = el("button", { class: "alert-notification-close" }, ["×"]);
    const dismissBtn = el("button", { class: "alert-btn dismiss" }, ["Dismiss"]);

    const card = el("div", { class: `alert-notification ${isBullish ? "bullish" : "bearish"}`, id }, [
      el("div", { class: "alert-notification-header" }, [
        el("div", { class: "alert-notification-title" }, [
          el("span", { class: "alert-notification-icon" }, [icon]),
          title,
        ]),
        closeBtn,
      ]),
      el("div", { class: "alert-notification-body" }, [
        el("div", { class: "alert-threshold" }, [
          `Ratio crossed ${direction} ${alert.threshold.value.toFixed(2)}`,
        ]),
        el("div", { class: "alert-ratio" }, [formatRatio(alert.ratio)]),
        el("div", { class: "alert-price" }, [`BTC Price: ${formatPrice(midPrice)}`]),
        el("div", { class: "alert-details" }, [timeText]),
        el("div", { class: "alert-buttons" }, [dismissBtn]),
      ]),
    ]);

    function dismiss(): void {
      card.style.animation = "slideIn 0.3s ease reverse";
      window.setTimeout(() => card.remove(), 300);
    }
    closeBtn.addEventListener("click", dismiss);
    dismissBtn.addEventListener("click", dismiss);

    container.append(card);

    // Real OS-level notification (Notification API), best-effort: only if
    // permission was already granted (never itself prompts -- prompting
    // happens exclusively via the audio-toggle opt-in above). This is the
    // "even if you're not looking at this tab" half of the ask; the
    // in-page card above is the primary, confirmed-grounded-in-legacy
    // behavior and does not depend on this succeeding.
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification(title, {
          body: `Ratio crossed ${direction} ${alert.threshold.value.toFixed(2)} -- now ${formatRatio(alert.ratio)} (BTC ${formatPrice(midPrice)})`,
        });
      }
    } catch {
      // Notification construction can throw in some contexts (e.g. no
      // service worker on some platforms) -- the in-page card already
      // covers the feature either way.
    }
  }

  return {
    root: container,
    audioToggle,
    fire(alert: FiredAlert, midPrice: number): void {
      showNotification(alert, midPrice);
      playAlertSound(alert.threshold.direction === "above");
    },
  };
}
